<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active">
                <i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <b-form-group class="col-sm-2">
            <EditForm
              :default-values="{ ...{ type: 1 } }"
              create
              title="Create user"
              @finish="handleItemsUpdate"
            >
              <template #button="{onclick}">
                <b-button type="button" variant="success" @click="onclick"
                  ><i class="fas fa-plus"></i> Create</b-button
                >
              </template>
            </EditForm>
          </b-form-group>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-4 col-md-4 col-sm-12">
            <b-form-group
              label-cols-lg="3"
              label-for="filter-username"
              label="Username: "
            >
              <b-form-input
                id="filter-username"
                type="search"
                v-model="filters.username"
                autocomplete="off"
                placeholder="Username"
                trim
              >
              </b-form-input>
            </b-form-group>
          </div>
          <div class="col-lg-4 col-md-4 col-sm-12">
            <b-form-group
              label-cols-lg="3"
              label-for="filter-email"
              label="Email: "
            >
              <b-form-input
                id="filter-email"
                type="search"
                v-model="filters.email"
                autocomplete="off"
                placeholder="Email"
                trim
              >
              </b-form-input>
            </b-form-group>
          </div>

          <div class="col-lg-4 col-md-4 col-sm-12">
            <b-form-group
              label-cols-lg="3"
              label-for="filter-operator"
              label="Role: "
            >
              <multiselect
                id="filter-role"
                v-model="filters.role"
                :options="
                  Object.keys(this.roles).map(roleId => {
                    return { value: roleId, label: this.roles[roleId] }
                  })
                "
                :custom-label="
                  opt =>
                    this.$store.getters['isShowDebugTools']
                      ? opt.value
                      : opt.label
                "
                track-by="value"
                :multiple="false"
                :placeholder="$t('Select')"
                :select-label="$t('Press enter to select')"
                :deselect-label="$t('Press enter to remove')"
                :selected-label="$t('Selected')"
              ></multiselect>
            </b-form-group>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <filter-buttons
              :cancel="this.cancel"
              :loadFn="loadItems"
              :cancelFn="cancelRequest"
              :resetFn="clearFilters"
            />
          </div>
        </b-row>

        <div class="row col-12" id="loading-container">
          <div class="col-12 mb-10">
            <div class="mb-10">
              <div class="table-wrapper mb-10">
                <b-table
                  :striped="true"
                  :small="true"
                  :hover="true"
                  :empty-text="emptyText"
                  :sort-by="sortBy"
                  :sort-desc="sortDesc"
                  show-empty
                  :busy="!!cancel"
                  :items="items"
                  :fields="fields"
                  :no-local-sorting="true"
                  :sort-icon-left="true"
                  @sort-changed="sortingChanged"
                >
                  <template #cell(tables)="row">
                    <span
                      v-if="!row.item.tables || row.item.tables.length === 0"
                      class="text-muted"
                    >
                      —
                    </span>
                    <span
                      v-else-if="row.item.tables.length <= 3"
                      class="badge badge-info mr-1"
                      v-for="tableId in row.item.tables"
                      :key="tableId"
                    >
                      {{ tableId }}
                    </span>
                    <span v-else>
                      <span
                        class="badge badge-info mr-1"
                        v-for="tableId in row.item.tables.slice(0, 3)"
                        :key="tableId"
                      >
                        {{ tableId }}
                      </span>
                      <b-badge
                        variant="secondary"
                        v-b-tooltip.hover
                        :title="getAllTablesText(row.item.tables)"
                        style="cursor: help;"
                      >
                        +{{ row.item.tables.length - 3 }}
                      </b-badge>
                    </span>
                  </template>

                  <template #cell(actions)="row">
                    <EditForm
                      :default-values="{ ...row.item }"
                      title="Edit user"
                      @finish="handleItemsUpdate"
                    ></EditForm>
                    <RemoveForm
                      :object="row.item.username"
                      message="Please confirm removing user"
                      :url="'/api/v1/users/' + row.item.id"
                      @finish="handleItemsUpdate"
                    ></RemoveForm>
                  </template>
                </b-table>
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <b-pagination
                  class="float-right"
                  v-model="pagination.page"
                  :total-rows="pagination.records"
                  :per-page="pagination.onPage"
                  @change="onPageChange"
                  first-number
                  last-number
                />
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <ve-pagination
                  class="float-right"
                  :page-index="pagination.page"
                  :page-size="pagination.onPage"
                  :total="pagination.records"
                  :pagingCount="pagination.count"
                  :layout="['total', 'sizer']"
                  :pageSizeOption="pagination.pageSizeOption"
                  @on-page-size-change="onPageSizeChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import EditUserForm from '../../components/Users/EditUserForm'
import RemoveForm from '../../components/remove-form/RemoveForm.vue'
import axios from 'axios'
import { getRouteIcon, ROUTE_USERS } from '../../utils/routerIcons'
import Multiselect from 'vue-multiselect'
import FilterButtons from '../../components/FilterButtons'
import { humanRoles } from '../../definitions/Roles'

function getDefaultPagination() {
  return {
    onPage: 25,
    page: 1,
    records: 0,
    count: 5,
    pageSizeOption: [25, 50, 100, 200]
  }
}

function getDefaultSort() {
  return {
    column: 'username',
    order: 'ASC'
  }
}

function getDefaultFilters() {
  return {
    username: null,
    email: null,
    role: null
  }
}

export default {
  components: {
    EditForm: EditUserForm,
    RemoveForm,
    Multiselect,
    FilterButtons
  },
  data() {
    return {
      sortBy: 'username',
      sortDesc: false,
      filters: getDefaultFilters(),
      cancel: null,
      error: null,
      roles: humanRoles,
      fields: [
        { key: 'username', label: 'User name', sortable: true },
        { key: 'email', label: 'E-mail', sortable: true },
        {
          key: 'type',
          label: 'Role',
          sortable: true,
          formatter: value => this.roles[value] ?? value
        },
        { key: 'tables', label: 'Tables', sortable: false },
        { key: 'actions', label: 'Actions' }
      ],
      items: [],
      sorts: getDefaultSort(),
      pagination: getDefaultPagination()
    }
  },
  computed: {
    emptyText() {
      if (this.cancel) {
        return 'Loading items..'
      }

      if (this.error) {
        return this.error
      }

      return 'There are no records to show'
    },
    routeIcon: () => getRouteIcon(ROUTE_USERS)
  },
  methods: {
    clearFilters() {
      this.filters = getDefaultFilters()
      this.sortingChanged({
        sortBy: getDefaultSort().column,
        sortDesc: getDefaultSort().order === 'DESC'
      })
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.pagination.page = 1

      this.loadItems()
    },
    handleItemsUpdate() {
      this.loadItems()
    },
    loadItems() {
      this.items = []

      let self = this

      this.$http({
        url: '/api/v1/users',
        params: {
          username: this.filters.username,
          email: this.filters.email,
          role: this.filters.role?.value,

          // pagination
          limit: this.pagination.onPage,
          offset: this.pagination.onPage * (this.pagination.page - 1),

          // sort params
          orderBy: this.sortBy,
          direction: this.sortDesc ? 'DESC' : 'ASC'
        },
        method: 'GET',
        cancelToken: new axios.CancelToken(c => {
          self.cancel = c
        })
      })
        .then(resp => {
          let {
            items,
            pagination: { limit, totalCount }
          } = resp.data

          this.items = items
          this.pagination.onPage = limit
          this.pagination.records = totalCount
        })
        .catch(err => {
          this.items = []
          this.error = err
        })
        .finally(() => {
          this.cancel = null
        })
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('this -> cancel previous request')
      }
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1
      this.pagination.onPage = pageSize
      this.loadItems()
    },
    onFinishEdit() {
      this.loadItems()
    },
    getAllTablesText(tables) {
      if (!tables || tables.length === 0) {
        return 'No tables'
      }
      return tables.join(', ')
    }
  },
  mounted() {
    this.loadItems()
  }
}
</script>
